/**
 * @file TasksByUser.jsx
 * @description Report page listing tasks assigned to a specific user, filterable by state.
 */

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { listTasks, getAllLabels } from "../../firebase/tasks";
import { collection, getDocs } from "firebase/firestore";
import { firestore } from "../../firebase/config";
import TaskSearchInput from "../../components/tms/TaskSearchInput";
import { LabelTag } from "../../components/tms/LabelTag";
import { describeRecurrence, isRepeatableTask } from "../../utils/recurrenceEngine";

// Helper for Priority Badges
const getPriorityBadgeClass = (priority) => {
  switch (priority) {
    case "Critical": // keeping for legacy
    case "High":
      return "bg-rose-100 text-rose-700 border border-rose-200 px-2.5 py-0.5 rounded-full text-xs font-bold tracking-wide inline-block";
    case "Medium":
      return "bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide inline-block";
    case "Low":
    default:
      return "bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide inline-block";
  }
};

// Helper for Status Badges
const getStatusBadgeClass = (status) => {
  switch (status) {
    case "Open":
      return "bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide inline-block";
    case "In Progress":
      return "bg-purple-50 text-purple-700 border border-purple-200 px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide inline-block";
    case "On Hold":
      return "bg-amber-100 text-amber-800 border border-amber-200 px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide inline-block";
    case "Closed":
    default:
      return "bg-slate-100 text-slate-700 border border-slate-300 px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide inline-block";
  }
};

const formatFirestoreDate = (timestamp) => {
  if (!timestamp) return "—";
  if (timestamp.toDate && typeof timestamp.toDate === "function") {
    return timestamp.toDate().toLocaleDateString();
  }
  return new Date(timestamp).toLocaleDateString();
};

export default function TasksByUser() {
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [labels, setLabels] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedRecurrence, setSelectedRecurrence] = useState("");
  const [usersLoading, setUsersLoading] = useState(true);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasApplied, setHasApplied] = useState(false);

  // Fetch Users on Mount
  useEffect(() => {
    const fetchUsers = async () => {
      setUsersLoading(true);
      try {
        const usersRef = collection(firestore, "users");
        const snapshot = await getDocs(usersRef);
        const list = snapshot.docs
          .map((docSnap) => ({
            uid: docSnap.id,
            name: docSnap.data().name || docSnap.data().email || docSnap.id,
            taskRole: docSnap.data().taskRole || "TaskUser",
            status: docSnap.data().status || "Active"
          }))
          // Exclude users with No Access role
          .filter((u) => u.taskRole !== "No Access" && u.taskRole !== "");

        setUsers(list);

        const labelsList = await getAllLabels();
        setLabels(labelsList);
      } catch (err) {
        console.error("Error fetching representative users for report:", err);
        setError("Failed to load user list.");
      } finally {
        setUsersLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const getAssigneeName = (uid) => {
    if (!uid) return "Unassigned";
    const matched = users.find((u) => u.uid === uid);
    return matched ? matched.name : "Unknown User";
  };

  const handleStateToggle = (stateName) => {
    if (selectedStates.includes(stateName)) {
      setSelectedStates(selectedStates.filter((s) => s !== stateName));
    } else {
      setSelectedStates([...selectedStates, stateName]);
    }
  };

  const handleApplyFilters = async (e) => {
    e.preventDefault();
    if (!selectedUserId) {
      setError("Please select a representative user to query tasks.");
      return;
    }

    setTasksLoading(true);
    setError(null);
    setHasApplied(true);

    try {
      const filters = {
        assignedTo: selectedUserId
      };
      if (selectedStates.length > 0) {
        filters.state = selectedStates;
      }

      const results = await listTasks(filters);
      // Client-side sort: newest task number first
      const sorted = results.sort((a, b) => b.taskNumber - a.taskNumber);
      setTasks(sorted);
    } catch (err) {
      console.error("Error querying tasks for representative:", err);
      setError("Failed to fetch tasks for the selected representative.");
    } finally {
      setTasksLoading(false);
    }
  };

  const inputStyle = {
    backgroundColor: "var(--theme-input-bg)",
    border: "1px solid var(--theme-input-border)",
    borderRadius: "var(--theme-input-radius)",
    color: "var(--theme-color-ink)",
  };

  const stateColors = {
    "Open": { active: { backgroundColor: "#2563eb", color: "#fff", borderColor: "#2563eb" } },
    "In Progress": { active: { backgroundColor: "#7c3aed", color: "#fff", borderColor: "#7c3aed" } },
    "On Hold": { active: { backgroundColor: "#d97706", color: "#fff", borderColor: "#d97706" } },
    "Closed": { active: { backgroundColor: "#475569", color: "#fff", borderColor: "#475569" } },
  };

  const labelColorMap = {};
  labels.forEach(l => {
    labelColorMap[l.name] = l.color;
  });

  return (
    <div
      className="container-fluid min-h-screen py-8 px-4 md:px-8"
      style={{ backgroundColor: "var(--theme-color-canvas)" }}
    >
      {/* Header */}
      <div
        className="mb-8 pb-5 flex flex-col md:flex-row justify-between items-start md:items-center"
        style={{ borderBottom: "1px solid var(--theme-color-mute)" }}
      >
        <div>
          <div className="flex flex-wrap items-center gap-4">
            <h1
              className="text-3xl font-extrabold tracking-tight m-0"
              style={{ color: "var(--theme-color-ink)", fontFamily: "var(--theme-font-family-display)" }}
            >
              Report: Tasks by User
            </h1>
            <TaskSearchInput />
          </div>
          <p className="mt-1 text-sm font-medium" style={{ color: "var(--theme-color-body)" }}>
            List and audit active tasks assigned to specific representatives, filterable by state.
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <Link
            to="/tasks/dashboard"
            className="inline-flex items-center justify-center font-semibold py-2 px-4 rounded-lg border transition-colors text-sm no-underline"
            style={{
              backgroundColor: "var(--theme-color-canvas-soft)",
              color: "var(--theme-color-body)",
              borderColor: "var(--theme-color-mute)",
            }}
          >
            ← Admin Dashboard
          </Link>
        </div>
      </div>

      {/* Filter Bar Card */}
      <div
        className="rounded-2xl border shadow-sm p-6 mb-8"
        style={{
          backgroundColor: "var(--theme-card-bg)",
          borderColor: "var(--theme-color-mute)",
          borderRadius: "var(--theme-card-radius)",
        }}
      >
        <form onSubmit={handleApplyFilters} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-end">
            {/* User Dropdown */}
            <div>
              <label
                className="block text-xs font-bold uppercase tracking-wider mb-2"
                style={{ color: "var(--theme-color-body-mid)" }}
              >
                Select Representative
              </label>
              {usersLoading ? (
                <div
                  className="h-9 animate-pulse rounded-xl"
                  style={{ backgroundColor: "var(--theme-color-canvas-soft)" }}
                ></div>
              ) : (
                <select
                  value={selectedUserId}
                  onChange={(e) => {
                    setSelectedUserId(e.target.value);
                    setError(null);
                    setHasApplied(false);
                    setTasks([]);
                  }}
                  className="w-full px-4 py-2.5 font-medium text-sm focus:outline-none focus:ring-2 transition-all"
                  style={inputStyle}
                >
                  <option value="">Choose a representative...</option>
                  {users.map((u) => (
                    <option key={u.uid} value={u.uid}>
                      {u.name}{u.status === "Inactive" ? " (Inactive)" : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Recurrence Dropdown */}
            <div>
              <label
                className="block text-xs font-bold uppercase tracking-wider mb-2"
                style={{ color: "var(--theme-color-body-mid)" }}
              >
                Recurrence
              </label>
              <select
                value={selectedRecurrence}
                onChange={(e) => setSelectedRecurrence(e.target.value)}
                className="w-full px-4 py-2.5 font-medium text-sm focus:outline-none focus:ring-2 transition-all cursor-pointer"
                style={inputStyle}
              >
                <option value="">All Tasks</option>
                <option value="recurring">🔄 Repeatable Only</option>
                <option value="non_recurring">One-off Only</option>
              </select>
            </div>

            {/* State Multi-Select Pills */}
            <div className="lg:col-span-2">
              <label
                className="block text-xs font-bold uppercase tracking-wider mb-2"
                style={{ color: "var(--theme-color-body-mid)" }}
              >
                Task States (Optional)
              </label>
              <div className="flex flex-wrap gap-x-4 gap-y-3 pt-1">
                {["Open", "In Progress", "On Hold", "Closed"].map((stateName) => {
                  const isActive = selectedStates.includes(stateName);
                  const count = tasks.filter((t) => t.status === stateName).length;
                  return (
                    <div key={stateName} className="flex flex-col items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleStateToggle(stateName)}
                        className="px-3.5 py-1.5 rounded-full text-xs font-bold border transition-all duration-200 cursor-pointer"
                        style={
                          isActive
                            ? stateColors[stateName].active
                            : {
                                backgroundColor: "var(--theme-color-canvas-soft)",
                                color: "var(--theme-color-body)",
                                borderColor: "var(--theme-color-mute)",
                              }
                        }
                      >
                        {stateName}
                      </button>
                      {hasApplied && (
                        <span 
                          className="text-xs font-bold"
                          style={{ color: "var(--theme-color-body-mid)" }}
                        >
                          {count}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div
            className="flex justify-end pt-3"
            style={{ borderTop: "1px solid var(--theme-color-mute)" }}
          >
            <button
              type="submit"
              disabled={tasksLoading || usersLoading}
              className="inline-flex items-center justify-center font-bold py-2.5 px-6 rounded-xl shadow-sm hover:shadow transition-colors text-sm cursor-pointer disabled:opacity-50"
              style={{
                backgroundColor: "var(--theme-button-primary-bg)",
                color: "var(--theme-button-primary-text)",
                borderRadius: "var(--theme-button-primary-radius)",
              }}
            >
              {tasksLoading ? "Querying..." : "Apply Filters"}
            </button>
          </div>
        </form>
      </div>

      {/* Error Panel */}
      {error && (
        <div
          className="max-w-xl mx-auto mb-8 px-4 py-3.5 rounded-2xl text-sm font-medium shadow-sm"
          style={{
            backgroundColor: "#fff5f5",
            border: "1px solid #feb2b2",
            color: "#c53030",
          }}
        >
          {error}
        </div>
      )}

      {/* Results Section */}
      {tasksLoading ? (
        /* Animated Table Skeleton */
        <div
          className="rounded-2xl border shadow-sm overflow-hidden animate-pulse"
          style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)" }}
        >
          <table className="w-full text-left border-collapse">
            <thead>
              <tr style={{ backgroundColor: "var(--theme-color-canvas-soft)", borderBottom: "1px solid var(--theme-color-mute)" }}>
                {[16, 48, 24, 24, 16, 16, 24, 24].map((w, i) => (
                  <th key={i} className="p-4">
                    <div className={`h-4 bg-slate-200 rounded w-${w}`}></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...Array(4)].map((_, idx) => (
                <tr key={idx} style={{ borderBottom: "1px solid var(--theme-color-canvas-soft)" }}>
                  <td className="p-4"><div className="h-4 bg-slate-200 rounded w-10"></div></td>
                  <td className="p-4"><div className="h-4 bg-slate-200 rounded w-72"></div></td>
                  <td className="p-4"><div className="h-4 bg-slate-200 rounded w-24"></div></td>
                  <td className="p-4"><div className="h-4 bg-slate-200 rounded w-28"></div></td>
                  <td className="p-4"><div className="h-6 bg-slate-200 rounded-full w-14"></div></td>
                  <td className="p-4"><div className="h-6 bg-slate-200 rounded-full w-16"></div></td>
                  <td className="p-4"><div className="h-4 bg-slate-200 rounded w-20"></div></td>
                  <td className="p-4"><div className="h-4 bg-slate-200 rounded w-20"></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : !hasApplied ? (
        /* Prompt to select user */
        <div
          className="rounded-2xl border shadow-sm py-16 px-4 text-center max-w-xl mx-auto"
          style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)" }}
        >
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ backgroundColor: "var(--theme-color-canvas-soft)", color: "var(--theme-color-primary)" }}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              ></path>
            </svg>
          </div>
          <h3 className="text-lg font-bold m-0" style={{ color: "var(--theme-color-ink)" }}>
            No Representative Selected
          </h3>
          <p className="mt-2 text-sm max-w-xs mx-auto leading-relaxed" style={{ color: "var(--theme-color-body)" }}>
            Please choose an active representative user from the dropdown filter at the top and apply filters to load active tasks.
          </p>
        </div>
      ) : tasks.length === 0 ? (
        /* Empty State */
        <div
          className="rounded-2xl border shadow-sm py-16 px-4 text-center max-w-xl mx-auto"
          style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)" }}
        >
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ backgroundColor: "var(--theme-color-canvas-soft)", color: "var(--theme-color-body-mid)" }}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2"
              ></path>
            </svg>
          </div>
          <h3 className="text-lg font-bold m-0" style={{ color: "var(--theme-color-ink)" }}>
            No Assigned Tasks Found
          </h3>
          <p className="mt-2 text-sm leading-relaxed max-w-sm mx-auto" style={{ color: "var(--theme-color-body)" }}>
            This representative currently has no tasks assigned to them matching the configured status constraints.
          </p>
        </div>
      ) : (
        <div>
          {/* Desktop Table View */}
          <div
            className="hidden lg:block rounded-2xl border shadow-sm overflow-hidden"
            style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)" }}
          >
            <div className="overflow-x-auto max-h-[600px]">
              <table className="w-full text-left border-collapse">
                <thead
                  className="sticky top-0 z-10"
                  style={{
                    backgroundColor: "var(--theme-color-canvas-soft)",
                    borderBottom: "1px solid var(--theme-color-mute)",
                  }}
                >
                  <tr className="text-xs font-bold uppercase tracking-wider" style={{ color: "var(--theme-color-body-mid)" }}>
                    <th className="p-4 pl-6 w-28"># Task</th>
                    <th className="p-4">Title</th>
                    <th className="p-4 w-44">Labels</th>
                    <th className="p-4">Assigned To</th>
                    <th className="p-4 w-32">State</th>
                    <th className="p-4 w-32">Priority</th>
                    <th className="p-4 w-36">Repeat</th>
                    <th className="p-4 w-40">Due Date</th>
                    <th className="p-4 pr-6 w-40">Created Date</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.filter((t) => {
                    if (selectedRecurrence === "recurring" && !isRepeatableTask(t)) return false;
                    if (selectedRecurrence === "non_recurring" && isRepeatableTask(t)) return false;
                    return true;
                  }).map((task) => (
                    <tr
                      key={task.id}
                      className="transition-colors"
                      style={{ borderBottom: "1px solid var(--theme-color-canvas-soft)" }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--theme-color-canvas-soft)")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                    >
                      <td className="p-4 pl-6 align-middle font-extrabold">
                        <Link
                          to={`/tasks/${task.taskNumber}`}
                          className="hover:underline no-underline"
                          style={{ color: "var(--theme-color-primary)" }}
                        >
                          {task.taskNumber}
                        </Link>
                      </td>
                      <td className="p-4 align-middle font-semibold max-w-sm truncate" style={{ color: "var(--theme-color-ink)" }}>
                        {task.title}
                      </td>
                      <td className="p-4 align-middle">
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {(task.labels || []).map((lName) => (
                            <LabelTag key={lName} name={lName} color={labelColorMap[lName]} />
                          ))}
                        </div>
                      </td>
                      <td className="p-4 align-middle text-sm font-medium" style={{ color: "var(--theme-color-body)" }}>
                        {getAssigneeName(task.assigneeUid)}
                      </td>
                      <td className="p-4 align-middle">
                        <span className={getStatusBadgeClass(task.status)}>{task.status}</span>
                      </td>
                      <td className="p-4 align-middle">
                        <span className={getPriorityBadgeClass(task.priority)}>{task.priority || "Low"}</span>
                      </td>
                      <td className="p-4 align-middle text-xs font-medium" style={{ color: "var(--theme-color-body)" }}>
                        {isRepeatableTask(task) ? (
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                              task.status === "Closed"
                                ? "text-gray-600 bg-gray-100"
                                : "text-indigo-700 bg-indigo-50"
                            }`}
                          >
                            <span>🔄</span>
                            <span>{describeRecurrence(task.recurrence)}</span>
                            {task.status === "Closed" && (
                              <span className="text-[10px] text-gray-500 font-normal ml-0.5">(Ended)</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="p-4 align-middle text-sm font-medium" style={{ color: "var(--theme-color-body)" }}>
                        {task.dueDate ? task.dueDate : "—"}
                      </td>
                      <td className="p-4 pr-6 align-middle text-sm font-medium" style={{ color: "var(--theme-color-body)" }}>
                        {formatFirestoreDate(task.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Stacked Card View */}
          <div className="lg:hidden space-y-4">
            {tasks.filter((t) => {
              if (selectedRecurrence === "recurring" && !isRepeatableTask(t)) return false;
              if (selectedRecurrence === "non_recurring" && isRepeatableTask(t)) return false;
              return true;
            }).map((task) => (
              <div
                key={task.id}
                className="rounded-2xl border shadow-sm p-5 space-y-3 transition-all"
                style={{
                  backgroundColor: "var(--theme-card-bg)",
                  borderColor: "var(--theme-color-mute)",
                  borderRadius: "var(--theme-card-radius)",
                }}
              >
                <div className="flex justify-between items-center">
                  <Link
                    to={`/tasks/${task.taskNumber}`}
                    className="text-xs font-extrabold hover:underline px-2.5 py-1 rounded"
                    style={{
                      color: "var(--theme-color-primary)",
                      backgroundColor: "var(--theme-color-canvas-soft)",
                    }}
                  >
                    #{task.taskNumber}
                  </Link>
                  <span className={getStatusBadgeClass(task.status)}>{task.status}</span>
                </div>

                <h3 className="text-base font-bold m-0 line-clamp-2" style={{ color: "var(--theme-color-ink)" }}>
                  {task.title}
                </h3>
                {task.labels && task.labels.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {task.labels.map((lName) => (
                      <LabelTag key={lName} name={lName} color={labelColorMap[lName]} />
                    ))}
                  </div>
                )}

                <div
                  className="pt-2 flex justify-between items-center text-xs"
                  style={{ borderTop: "1px solid var(--theme-color-canvas-soft)" }}
                >
                  <div>
                    <span className="block font-medium" style={{ color: "var(--theme-color-body-mid)" }}>Assigned To</span>
                    <span className="font-semibold" style={{ color: "var(--theme-color-ink)" }}>
                      {getAssigneeName(task.assigneeUid)}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="block font-medium" style={{ color: "var(--theme-color-body-mid)" }}>Due Date</span>
                    <span className="font-semibold" style={{ color: "var(--theme-color-ink)" }}>
                      {task.dueDate || "—"}
                    </span>
                  </div>
                </div>

                <div className="flex justify-between items-center text-xs">
                  <div>
                    <span className="block font-medium" style={{ color: "var(--theme-color-body-mid)" }}>Priority</span>
                    <span className={getPriorityBadgeClass(task.priority)}>{task.priority || "Low"}</span>
                  </div>
                </div>

                <div
                  className="pt-1 flex justify-between items-center text-xs font-medium"
                  style={{ color: "var(--theme-color-body-mid)" }}
                >
                  <span>Created Date</span>
                  <span className="font-semibold" style={{ color: "var(--theme-color-body)" }}>
                    {formatFirestoreDate(task.createdAt)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
